import { createSelector } from 'reselect'
import { findPostPurchaseCheckout, isBedbankItem, isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { canShowAddFlightsForHotelItems, checkoutAccommodationOfferView, isCheckoutAccommodationItemsOnly } from './view/accommodation'
import { getFlightItems } from './view/flights'
import { getVillaViews } from 'checkout/selectors/view/villa'
import { getCanRedeemLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { getTourV1Items } from './view/tourv1'
import { checkIsExperienceGiftOfferOrderMode } from 'checkout/selectors/view/experience'
import { getFunnelFeatures } from './funnel'

export const isFlightsPendingToAddToCart = createSelector(
  (state: App.State) => getFlightItems(state),
  (flightItems): boolean => {
    return flightItems.length > 0 &&
      flightItems.some(item => {
        if (item.fareType === 'return') {
          return !item.flights[0] || !item.flights[1]
        } else if (item.fareType === 'oneWay') {
          return !item.flights[0]
        }
      })
  },
)

export const hasIncompleteTourItem = createSelector(
  (state: App.State) => getTourV1Items(state),
  item => item.some(item => !item.packageId || !item.startDate || !item.endDate),
)

export const hasInsuranceItem = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder?.insuranceItems,
  (items) => {
    if (!items?.length) {
      return false
    }

    return items.some(item => item.status !== 'cancelled')
  },
)

export const isExperiencesSupportedToAddToCart = createSelector(
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => getVillaViews(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getFunnelFeatures(state),
  (accommodationViews, villaViews, postPurchase, funnelFeatures): boolean => {
    if (!funnelFeatures.experiences) {
      return false
    }

    // Experience-only BNBL should skip ExperiencesPage (only used for adding experiences to hotel checkout)
    const hasAccommodation = accommodationViews.data.length > 0 || villaViews.data.length > 0
    if (postPurchase === 'select-date' && !hasAccommodation) {
      return false
    }

    if (!postPurchase || postPurchase === 'select-date') {
      if (hasAccommodation) {
        return accommodationViews.data.every(view => view.itemViews.every(itemView => isInstantBookingLEHotelItem(itemView.item) || isBedbankItem(itemView.item))) ||
          villaViews.data.length > 0
      }
    }

    return false
  },
)

export const isLuxPlusAvailableToAddToCart = createSelector(
  (state: App.State) => getIsLuxPlusEnabled(state),
  (state: App.State) => getCanRedeemLuxPlusBenefits(state),
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.auth.account.luxPlus.member.disableMembership,
  (state: App.State) => checkIsExperienceGiftOfferOrderMode(state),
  (state: App.State) => getFunnelFeatures(state),
  (enabled, canRedeemLuxPlusBenefits, isGift, isPostPurchase, disableMembership, isExperienceGiftOfferOrderMode, funnelFeatures): boolean => {
    const shouldSeeUpsell = !canRedeemLuxPlusBenefits
    return !!(enabled && shouldSeeUpsell && (isExperienceGiftOfferOrderMode || !isGift) && !isPostPurchase && !disableMembership && funnelFeatures.luxPlus)
  },
)

export const isAddFlightsStepAvailable = createSelector(
  (state: App.State) => isCheckoutAccommodationItemsOnly(state),
  (state: App.State) => canShowAddFlightsForHotelItems(state),
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getFunnelFeatures(state),
  (state: App.State) => isFlightsPendingToAddToCart(state),
  (isHotelItemsOnly, isFlashHotelItemsValid, isGift, isPostPurchase, funnelFeatures, hasFlightsPending): boolean => {
    // Allow the step if cart has only accommodation items OR has pending flights to add
    const hasValidCartState = isHotelItemsOnly || hasFlightsPending
    return hasValidCartState && isFlashHotelItemsValid && !isGift && !isPostPurchase && funnelFeatures.flights
  },
)
